package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import br.gov.go.pge.permanenciaweb.shared.EventDto
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import br.gov.go.pge.permanenciaweb.site.pages.DashboardState
import br.gov.go.pge.permanenciaweb.site.pages.loadEvents
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

val EventsViewStyle = CssStyle.base {
    Modifier.padding(top = 0.cssRem, right = 1.5.cssRem, bottom = 1.5.cssRem, left = 1.5.cssRem)
}

val EventsFooterStyle = CssStyle.base {
    Modifier
        .margin(0.cssRem)
        .padding(0.5.cssRem, 1.cssRem)
        .fontSize(0.75.cssRem)
        .color(IndustrialPalette.Muted)
}

@Composable
internal fun EventsPanel(
    state: DashboardState,
    facets: Map<FilterKey, List<String>>,
) {
    // Runs on tab entry and whenever the server announces that new events were ingested.
    LaunchedEffect(state.eventsVersion) {
        state.loadEvents()
    }

    val rows = state.events
        .applyColumnFilters(state.filters.forTable(EVENTS_FILTER_KEYS)) { e, key -> key.eventText(e) }
        .sortedWithCriteria(state.eventsSort) { e, field -> field.text(e) }
    val visible = rows.take(state.eventsVisibleCount)

    Section(attrs = EventsViewStyle.toModifier().then(Modifier.id("events-view")).toAttrs()) {
        Panel {
            PanelHeader(
                title = {
                    Text("Eventos de hoje ")
                    Span {
                        Text(
                            when {
                                state.events.isEmpty() -> ""
                                rows.size != state.events.size -> "— ${rows.size} de ${state.events.size}"
                                else -> "— ${rows.size}"
                            }
                        )
                    }
                },
                actions = { ClearFiltersButton(state, EVENTS_SCOPE) },
            )
            EventsTable(
                events = visible,
                sort = state.eventsSort,
                filters = state.filters,
                facets = facets,
                onSort = { field, shift -> state.toggleEventsSort(field, shift) },
                onFilterChange = { key, filter -> state.updateFilter(key, filter) },
            )
            if (visible.size < rows.size) {
                LoadMoreSentinel(resetKey = rows) { state.loadMoreEvents(rows.size) }
                P(attrs = EventsFooterStyle.toAttrs()) {
                    Text("Mostrando ${visible.size} de ${rows.size} — role para carregar mais")
                }
            }
            if (state.hasFilters(EVENTS_SCOPE) && rows.isEmpty()) {
                P(attrs = PanelEmptyStyle.toAttrs()) { Text("Nenhum evento para os filtros atuais.") }
            }
        }
    }
}

@Composable
private fun EventsTable(
    events: List<EventDto>,
    sort: List<Criterion<EventSortField>>,
    filters: Map<FilterKey, ColumnFilter>,
    facets: Map<FilterKey, List<String>>,
    onSort: (EventSortField, Boolean) -> Unit,
    onFilterChange: (FilterKey, ColumnFilter) -> Unit,
) {
    Table(attrs = TableStyle.toAttrs()) {
        Thead {
            Tr {
                EventHeaderCell("Horário", EventSortField.HORARIO, sort, filters, facets, onSort, onFilterChange, numeric = true)
                EventHeaderCell("Sentido", EventSortField.SENTIDO, sort, filters, facets, onSort, onFilterChange)
                EventHeaderCell("Pessoa", EventSortField.PESSOA, sort, filters, facets, onSort, onFilterChange)
                EventHeaderCell("Cad. Portaria", EventSortField.TIPO, sort, filters, facets, onSort, onFilterChange)
                EventHeaderCell("Setor", EventSortField.SETOR, sort, filters, facets, onSort, onFilterChange)
                EventHeaderCell("Andar", EventSortField.ANDAR, sort, filters, facets, onSort, onFilterChange)
                EventHeaderCell("Sala", EventSortField.SALA, sort, filters, facets, onSort, onFilterChange)
                EventHeaderCell("Unidade", EventSortField.UNIDADE, sort, filters, facets, onSort, onFilterChange)
                EventHeaderCell("Fonte", EventSortField.FONTE, sort, filters, facets, onSort, onFilterChange)
                EventHeaderCell("Dispositivo", EventSortField.DISPOSITIVO, sort, filters, facets, onSort, onFilterChange)
            }
        }
        Tbody {
            for (e in events) {
                Tr(attrs = TableRowHoverStyle.toAttrs()) {
                    Td(attrs = TableNumericCellStyle.toAttrs()) { Text(e.horario) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(e.sentido) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(e.pessoa) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(e.tipoAcesso.orEmpty()) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(e.setor.orEmpty()) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(e.andar.orEmpty()) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(e.sala.orEmpty()) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(e.unidade.orEmpty()) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(e.fonteNome.orEmpty()) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(e.dispositivo.orEmpty()) }
                }
            }
        }
    }
}

@Composable
private fun EventHeaderCell(
    label: String,
    field: EventSortField,
    sort: List<Criterion<EventSortField>>,
    filters: Map<FilterKey, ColumnFilter>,
    facets: Map<FilterKey, List<String>>,
    onSort: (EventSortField, Boolean) -> Unit,
    onFilterChange: (FilterKey, ColumnFilter) -> Unit,
    numeric: Boolean = false,
) = SortableTh(
    label = label,
    field = field,
    criteria = sort,
    filter = filters[field.filterKey] ?: ColumnFilter.Empty,
    filterId = "events-${field.name.lowercase()}",
    options = facets[field.filterKey],
    numeric = numeric,
    onSort = onSort,
    onFilterChange = { onFilterChange(field.filterKey, it) },
)
